import {
  STOCK_TRANSFER_STATUS_LABELS,
  STOCK_TRANSFER_STATUSES,
  type StockTransfer,
  stockTransferStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Truck } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { moneyExport } from "../../../../features/market/market-values.js";
import {
  type TransferFilters,
  transfersQuery,
  useMarketPermissions,
  useTransfers,
} from "../../../../features/market/queries.js";
import {
  LedgerStatus,
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  TransferStatusBadge,
  useMarketStations,
} from "../../../../features/market/shared.js";
import { TransferDialog } from "../../../../features/market/transfer-dialog.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatCount, formatDate, formatDateTime } from "../../../../lib/format.js";

const searchSchema = z.object({
  ...marketSearchShape,
  direction: z.enum(["OUTGOING", "INCOMING"]).optional().catch(undefined),
  status: stockTransferStatusSchema.optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/transferler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: TransfersRoute,
});

/**
 * İstasyonlar arası transferler (Prompt 16 — web). Akış: taslak → gönder (gönderen istasyon, stok çıkar) → teslim
 * al (alan istasyon, stok girer; eksik gelen fire olur ve fiş kesilir). Yalnızca AYNI şirketin istasyonları
 * arasındadır. Yazma `market.stock.manage`; istasyon kapsamı sunucudadır.
 */
function TransfersRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketTransfers")}
      description={t("market.transfers.description")}
      breadcrumb={[{ label: t("menu.marketTransfers") }]}
    >
      {({ companyId }) => <TransfersBody companyId={companyId} />}
    </MarketPage>
  );
}

function TransfersBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useMarketPermissions();
  const station = useMarketStations(companyId);
  const [creating, setCreating] = useState(false);

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: TransferFilters = {
    stationId: station.stationId ?? undefined,
    direction: search.direction,
    status: search.status,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useTransfers(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<StockTransfer>[]>(
    () => [
      {
        id: "transferNo",
        header: t("market.transfers.fields.transferNo"),
        hideable: false,
        exportValue: (row) => row.transferNo,
        cell: (row) => (
          <Link
            to="/market/transferler/$id"
            params={{ id: row.id }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span className="font-mono whitespace-nowrap">{row.transferNo}</span>
            <span className="text-xs text-muted-foreground">{formatDate(row.createdAt)}</span>
          </Link>
        ),
      },
      {
        id: "route",
        header: t("market.transfers.fields.route"),
        exportValue: (row) => `${row.fromStationName} → ${row.toStationName}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>
              {row.fromStationName} <span aria-hidden="true">→</span>
              <span className="sr-only">{t("market.transfers.toSr")}</span> {row.toStationName}
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              {row.fromWarehouseCode} → {row.toWarehouseCode}
            </span>
          </span>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => STOCK_TRANSFER_STATUS_LABELS[row.status],
        cell: (row) => <TransferStatusBadge status={row.status} />,
      },
      {
        id: "lineCount",
        header: t("market.fields.lineCount"),
        align: "right",
        exportValue: (row) => row.lineCount,
        cell: (row) => <span className="tabular">{formatCount(row.lineCount)}</span>,
      },
      {
        id: "sentValue",
        header: t("market.transfers.fields.sentValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.sentValue),
        cell: (row) => <MoneyText value={row.sentValue} />,
      },
      {
        id: "receivedValue",
        header: t("market.transfers.fields.receivedValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.receivedValue),
        cell: (row) => <MoneyText value={row.receivedValue} />,
      },
      {
        id: "shortValue",
        header: t("market.transfers.fields.shortValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.shortValue),
        cell: (row) => <MoneyText value={row.shortValue} />,
      },
      {
        id: "sentAt",
        header: t("market.transfers.fields.sentAt"),
        defaultVisible: false,
        exportValue: (row) => row.sentAt,
        cell: (row) => <span className="tabular">{formatDateTime(row.sentAt)}</span>,
      },
      {
        id: "receivedAt",
        header: t("market.transfers.fields.receivedAt"),
        defaultVisible: false,
        exportValue: (row) => row.receivedAt,
        cell: (row) => <span className="tabular">{formatDateTime(row.receivedAt)}</span>,
      },
      {
        id: "ledger",
        header: t("market.fields.ledger"),
        exportValue: (row) => row.ledgerStatus,
        cell: (row) => <LedgerStatus status={row.ledgerStatus} journalEntryId={row.journalEntryId} />,
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;
  const canTransfer = station.stations.length > 1;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        {station.stationId && station.showPicker ? (
          <FormField label={t("market.transfers.direction")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.direction ?? ""}
                onChange={(event) => setSearch({ direction: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                <option value="OUTGOING">{t("market.transfers.outgoing")}</option>
                <option value="INCOMING">{t("market.transfers.incoming")}</option>
              </Select>
            )}
          </FormField>
        ) : null}
        {permissions.canManageStock && canTransfer ? (
          <Button className="ms-auto" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("market.transfers.new")}
          </Button>
        ) : null}
      </Card>
      {canTransfer ? null : <Alert tone="info">{t("market.transfers.singleStation")}</Alert>}

      <DataTable
        caption={t("market.transfers.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Truck />}
            title={t("market.transfers.emptyTitle")}
            description={t("market.transfers.emptyDescription")}
          />
        }
        filters={
          <FormField label={t("common.status")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? ""}
                onChange={(event) => setSearch({ status: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {STOCK_TRANSFER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {STOCK_TRANSFER_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<StockTransfer>(
            async (cursor) =>
              (
                await api.GET("/market/transfers", {
                  params: { query: transfersQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "market-transferler",
            sheetName: t("menu.marketTransfers"),
            columns: visible,
            rows,
          });
        }}
      />

      {permissions.canManageStock && canTransfer ? (
        <TransferDialog
          open={creating}
          onOpenChange={setCreating}
          companyId={companyId}
          stations={station.stations}
          defaultFromStationId={station.stationId}
          onSaved={(saved) => void navigate({ to: "/market/transferler/$id", params: { id: saved.id } })}
        />
      ) : null}
    </>
  );
}
